// Strip Microsoft Office / Google Sheets noise from clipboard HTML before
// running it through Plate's HTML deserializer. Office variants leak:
//   - <!--[if ...]>...<![endif]--> conditional comments
//   - <!--td { ... } br { ... }--> CSS-in-comments (this is the bit that
//     was rendering as literal text inside the editor)
//   - <o:*>, <w:*>, <m:*>, <v:*>, <x:*> namespaced tags
//   - mso-* inline CSS properties + class="MsoNormal" / xmlns:* attrs
//   - <style> blocks at the top of the fragment
//   - smart-tag <st1:*> wrappers
//
// We also strip every `font-family: ...;` and `<font face=...>` attribute
// so pasted content always renders in the editor's default Gilroy stack —
// otherwise pasting from Word / Docs drops "Calibri" / "Arial" inline
// styles into our document and the editor becomes a font Frankenstein.
// font-size + color stay, only font-family is normalised away.
//
// We do this purely textually, BEFORE handing the string to DOMParser, so
// the parser never sees the garbage to begin with.

export function sanitizeOfficeHtml(html: string): string {
    return (
        html
            .replace(/<!--[\s\S]*?-->/g, '')
            // Word's downlevel-revealed conditional comments use the `<![if]>...<![endif]>`
            // form (not the `<!--[if]-->` form the rule above matches). They wrap
            // list-bullet glyphs + nbsp spacers that otherwise leak as literal text.
            // Remove the whole construct first, then any stray bare markers.
            .replace(/<!\[if[^\]]*\]>[\s\S]*?<!\[endif\]>/gi, '')
            .replace(/<!\[(?:if|endif)[^\]]*\]>/gi, '')
            .replace(/<\?xml[^>]*\?>/gi, '')
            .replace(/<\/?(?:o|w|m|v|x|st\d):[^>]+>/gi, '')
            .replace(/<style[\s\S]*?<\/style>/gi, '')
            // class=Mso... in double-quoted, single-quoted, or unquoted form.
            .replace(/\sclass=(?:"Mso[^"]*"|'Mso[^']*'|Mso[^\s>]*)/gi, '')
            // mso-* style property, including when it leads the `style="..."`
            // value (preceded by a quote, not a space — the original `\s`-only
            // rule missed that case). Preserve the leading delimiter so the
            // surrounding attribute quote / separator is not consumed.
            .replace(/(^|[\s;"'])mso-[a-z-]+:[^;"']*;?/gi, '$1')
            .replace(/\sxmlns(?::[a-z]+)?="[^"]*"/gi, '')
            // Strip font-family from inline `style="..."` (matches with or
            // without leading semicolon / quotes around the value). The value
            // matcher consumes HTML entities (`&quot;`/`&#34;`/`&apos;`) as whole
            // tokens so the `;` that terminates an entity is NOT mistaken for the
            // CSS declaration separator. Word emits font-family with `&quot;`-
            // escaped quotes (`font-family: &quot;Baskerville Old Face&quot;, …`);
            // the old `[^;"']*` halted inside the first entity and left a mangled
            // `Baskerville Old Face&quot;, …;` fragment in the `style`, corrupting
            // the whole declaration so a sibling `font-size` on the same span was
            // lost (pasted Word list text dropped to the default body size).
            .replace(/font-family\s*:\s*(?:&[a-z0-9#]+;|[^;"'])*;?/gi, '')
            // Strip the legacy <font face="..."> attribute.
            .replace(/\sface\s*=\s*"[^"]*"/gi, '')
            .replace(/\sface\s*=\s*'[^']*'/gi, '')
    );
}
